import { useMemo } from "react"
import { brand } from "@/assets/brand"
import type { DraftReviewReport } from "@/features/oio/lib/review"
import { UNVERIFIED_STATUSES } from "@/features/oio/lib/review"
import {
  VERDICT_PILL,
  aggregateConfirmedDemand,
  formatINR,
  mapDeterminationStatus,
  type FindingsSummaryItem,
} from "@/features/oio/lib/findings"

/**
 * The completion summary the assistant "says" in the chat once the order is
 * drafted — a LOCALLY-RENDERED card styled like an assistant message (.msg
 * idiom). It is NOT a message sent through CopilotKit, so it never enters the
 * runtime's message history; it derives from state on every render, which is
 * also what makes it survive refresh with zero storage of its own.
 *
 * Dedupe: the card is a single derived node — the parent renders exactly one,
 * keyed on thread_id + draft_revision, so re-renders/status flaps can never
 * append duplicates and a regenerated draft simply re-keys the same card.
 */
interface OrderSummaryCardProps {
  threadId: string
  draftRevision: number | null
  findings: FindingsSummaryItem[]
  reviewReport?: DraftReviewReport | null
}

/** Count of review-layer items needing officer attention; null if no report. */
export function countReviewFlags(report: DraftReviewReport | null | undefined): number | null {
  if (!report) return null
  // ONLY the flags the officer can actually open and act on — the unverified
  // citations that surface as chips in the Analysis tab. threshold_findings
  // and validity_warnings are computed but not yet rendered anywhere, so
  // counting them pointed the officer at review items that don't exist.
  return report.citation_reviews.length > 0
    ? report.citation_reviews.filter((c) => UNVERIFIED_STATUSES.has(c.verification_status)).length
    : report.unverified_citation_count
}

export function OrderSummaryCard({
  threadId,
  draftRevision,
  findings,
  reviewReport,
}: OrderSummaryCardProps) {
  const aggregate = useMemo(() => aggregateConfirmedDemand(findings), [findings])
  const flagged = countReviewFlags(reviewReport)

  if (findings.length === 0) return null

  return (
    <div
      className="msg"
      data-testid="order-summary-card"
      data-summary-key={`${threadId}:${draftRevision ?? 0}`}
    >
      <div className="msg__av msg__av--ai">
        <img className="msg__av-logo" src={brand.emblem} alt="" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="msg__name">NitiAssist AI</div>
        <div className="msg__body">
          <p>
            <b>The order is drafted.</b> Head-wise, here is where it lands:
          </p>
          <ul className="ordersum__list">
            {/* A pathological run can carry hundreds of heads; the card is a
                summary, not a ledger — cap the rows so one bad artifact
                cannot freeze the chat pane. */}
            {findings.slice(0, 30).map((f) => {
              const status = mapDeterminationStatus(f.determination_status)
              const pill = VERDICT_PILL[status]
              const demand = Number(f.demand_computation?.total_demand) || 0
              return (
                <li key={f.allegation_id} data-testid={`summary-head-${f.allegation_id}`}>
                  <span className="ordersum__id">{f.allegation_id}</span>
                  <span className={"pill pill--" + pill.cls}>{pill.label}</span>
                  <span className="ordersum__amt">
                    {status === "dropped" ? "dropped" : demand > 0 ? formatINR(demand) : "—"}
                  </span>
                </li>
              )
            })}
            {findings.length > 30 && (
              <li className="ordersum__more">…and {findings.length - 30} more heads (see the order)</li>
            )}
          </ul>
          {aggregate && aggregate.allDropped && (
            <p data-testid="summary-aggregate">
              No demand survives — every head stands dropped.
            </p>
          )}
          {aggregate && !aggregate.allDropped && (
            <p data-testid="summary-aggregate">
              Total confirmed: <b>{formatINR(aggregate.tax)}</b> tax
              {aggregate.penalty > 0 && (
                <>
                  {" "}
                  + <b>{formatINR(aggregate.penalty)}</b> penalty
                </>
              )}
              .
            </p>
          )}
          <p>
            {flagged != null && flagged > 0
              ? `The Analysis tab flags ${flagged} citation${flagged === 1 ? "" : "s"} to verify before you sign; review the order in the draft pane.`
              : "Review the order in the draft pane — no citations were flagged for verification."}
          </p>
        </div>
      </div>
    </div>
  )
}
